import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CatchRegistrationFormHeader, CatchRegistrationFormBody, CatchRegistrationFormWrapper, RowContainer, ChildWrapper, ChildData, MainRowContainer } from './CatchRegistrationModal.styled';
import { Button, Avatar, Box, Step, StepContent, StepLabel, Stepper, Typography, Dialog, DialogContent, useTheme, IconButton, useMediaQuery, Tooltip } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import User from '../../../../Models/UserModels/User';
import PostCatchBody from '../../../../Models/CatchRegistrationModels/PostCatchBody';
import { deleteWeight, patchWeight, postDeduction, postWeight } from '../../../../Services/LondunarkerfiAPIService';
import CatchRegistrationItem from '../../../../Models/CatchRegistrationModels/CatchRegistration';
import CatchRegistrationCatch from './CatchRegistrationCatch/CatchRegistrationCatch';
import CatchRegistrationWeight from './CatchRegistrationWeight/CatchRegistrationWeight';
import CatchRegistrationDeduction from './CatchRegistrationDeduction/CatchRegistrationDeduction';
import Vehicle from '../../../../Models/CatchRegistrationModels/Vehicle';
import WeightNote from '../../../../Models/WeightNoteModels/WeightNote';
import { LoadingButton } from '@mui/lab';
import CatchCombination from '../../../../Models/CatchRegistrationModels/CatchCombination';
import FishingArea from '../../../../Models/CatchRegistrationModels/FishingArea';
import StorageMethod from '../../../../Models/CatchRegistrationModels/StorageMethod';
import Destiny from '../../../../Models/CatchRegistrationModels/Destiny';
import FishingStock from '../../../../Models/CatchRegistrationModels/FishingStock';
import Condition from '../../../../Models/CatchRegistrationModels/Condition';
import Gear from '../../../../Models/WeightNoteModels/Gear';
import DeductionType from '../../../../Models/CatchRegistrationModels/DeductionType';
import WeightNoteTypes from '../../../../Constants/WeightNoteTypes';
import CatchDeduction from '../../../../Models/CatchRegistrationModels/CatchDeduction';
import PostDeductionBody from '../../../../Models/CatchRegistrationModels/PostDeductionBody';
import CatchRegistrationProductCalcModal from './CatchRegistrationProductCalc/CatchRegistrationProductCalcModal';
import { MobilePaperComponent, PaperComponent } from '../../../../SharedComponents/Paper/CustomPaper';
import CatchRegistrationDefaults from '../../../../Constants/CatchRegistrationDefaults';
import { logError } from '../../../../Helpers/LogError';
import { AuthenticationUnavailableError, SessionExpiredError } from '../../../../Services/AuthService';
import { useConfirm } from 'material-ui-confirm';
import CatchRegistrationSplitModal from './CatchRegistrationSplitModal/CatchRegistrationSplitModal';
import CatchRegistrationMoveModal from './CatchRegistrationMoveModal/CatchRegistrationMoveModal';
import CatchRegistrationModalNavigator from './CatchRegistrationModalNavigator.tsx/CatchRegistrationModalNavigator';

type PrefilledField = 'fishingAreaId' | 'storageMethodId' | 'destinyId';
type PrefillMap = Partial<Pick<PostCatchBody, PrefilledField>>;
const PREFILL_FIELDS: PrefilledField[] = ['fishingAreaId', 'storageMethodId', 'destinyId'];
const PREFILL_FIELD_SET = new Set<PrefilledField>(PREFILL_FIELDS);

interface CatchRegistrationModalProps {
  weightItem: CatchRegistrationItem | undefined;
  setNewWeightItem: (weightId: number) => Promise<void>;
  resetWeightItem: () => void;
  refetchVehicles: () => Promise<void>;
  selectedCarouselIndex: number | undefined;
  sortedWeights: CatchRegistrationItem[] | undefined;
  setIndex: (index: number | undefined) => void;
  open: boolean;
  catchCombinations: CatchCombination[] | null;
  fishAreas: FishingArea[];
  storageMethods: StorageMethod[];
  destinies: Destiny[];
  fishingStocks: FishingStock[];
  conditions: Condition[];
  vehicles: Vehicle[];
  gears: Gear[];
  deductionTypes: DeductionType[]
  selectedWeightNote: WeightNote;
  showSnackbar: (message: string, severity: string) => void;
  toggleCatchOpen: () => void;
  user: User;
}

/**
 * Functional component for CatchRegistrationModal.
 * Displays the modal for catch registration.
 * @param props 
 * - takes in various values used for dropDown values.
 * - takes in the selected landing and the selected weightNote.
 * - takes in a function to fetch catches.
 * - takes in a function to set the next catch item.
 * - takes in a function to set the previous catch item.
 * @returns {JSX} - Responsible for returning the modal which is used to add, edit and view catches.
 * should render the following components:
 * - CatchRegistrationCatch
 * - CatchRegistrationWeight
 * - CatchRegistrationVehicle
 * - CatchRegistrationDeduction
 */

const CatchRegistrationModal: FC<CatchRegistrationModalProps> = (props) => {
  const [formData, setFormData] = useState<PostCatchBody>(Object);
  const [prefilledDefaults, setPrefilledDefaults] = useState<PrefillMap>({});
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const stockInputRef = useRef<HTMLInputElement | null>(null);
  const [activeStep, setActiveStep] = React.useState(0);
  const [currentTotalWeight, setCurrentTotalWeight] = useState<number>(0);
  const [tempDeductions, setTempDeductions] = useState<CatchDeduction[]>([]);
  const [icePercentageChanged, setIcePercentageChanged] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [productModalOpen, setProductModalOpen] = useState<boolean>(false);
  const [deductionModalOpen, setDeductionModalOpen] = useState<boolean>(false);
  const [autoFocusVehicle, setAutoFocusVehicle] = useState<boolean>(false);
  const [splitModalOpen, setSplitModalOpen] = useState<boolean>(false);
  const [moveModalOpen, setMoveModalOpen] = useState<boolean>(false);
  const fallbackRef = useRef<HTMLInputElement | null>(null);
  const lastWeightItemIdRef = useRef<number | undefined>(undefined);
  // Tracks the special case where ProductCalc was auto-opened on row click.
  // When that calc is confirmed we also close this modal; manual opens keep it open.
  const autoOpenedProductCalcRef = useRef<boolean>(false);
  const confirm = useConfirm();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [stockFocusTrigger, setStockFocusTrigger] = useState(0);
  const F8_KEY_CODE = 119;
  const whaleFishingStockIds = useMemo(() => new Set([
    CatchRegistrationDefaults.FISHING_STOCK_FIN_WHALE_ID,
    CatchRegistrationDefaults.FISHING_STOCK_MINKE_WHALE_ID
  ]), []);
  const selectedFishingStock = useMemo(() => {
    const stockId = formData.fishStockId ?? props.weightItem?.fishingStock?.id;
    return props.fishingStocks.find(stock => stock.id === stockId) ?? props.weightItem?.fishingStock;
  }, [formData.fishStockId, props.fishingStocks, props.weightItem?.fishingStock]);
  const showAnimalCount = selectedFishingStock?.id !== undefined &&
    whaleFishingStockIds.has(selectedFishingStock.id);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !deductionModalOpen && !productModalOpen && !splitModalOpen) {
        event.preventDefault();
        if (submitButtonRef.current) {
          submitButtonRef.current.click();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [deductionModalOpen, productModalOpen, splitModalOpen]);

  useEffect(() => {
    // Keyboard shortcuts
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const keyboardNewCatch = (e: any) => {
      if ((e.keyCode) === F8_KEY_CODE && !loading && props.weightItem) {
        e.preventDefault();
        handleNew();
      }
    };
    window.addEventListener('keydown', keyboardNewCatch);
    return () => {
      window.removeEventListener('keydown', keyboardNewCatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, props.weightItem]);

  useEffect(() => {
    const newId = props.weightItem?.id;
    const lastId = lastWeightItemIdRef.current;

    if (newId !== lastId) {
      if (props.weightItem) {
        setCurrentTotalWeight(props.weightItem.quantity);
        setFormData({});
        setPrefilledDefaults({});
        setIcePercentageChanged(false);
      } else {
        setCurrentTotalWeight(0);
      }
      lastWeightItemIdRef.current = newId;
      return;
    }

    if (props.weightItem) {
      setCurrentTotalWeight(props.weightItem.quantity);
    }
  }, [props.weightItem]);

  const getOriginalFieldValue = useCallback((field: keyof PostCatchBody) => {
    if (!props.weightItem) {
      return undefined;
    }
    switch (field) {
      case 'fishingAreaId':
        return props.weightItem.fishingArea?.id;
      case 'storageMethodId':
        return props.weightItem.storageMethod?.id;
      case 'destinyId':
        return props.weightItem.destiny?.id;
      case 'fishStockId':
        return props.weightItem.fishingStock?.id;
      case 'conditionId':
        return props.weightItem.condition?.id;
      case 'isUnderKilogram':
        return props.weightItem.underKilogram;
      case 'weight':
        return props.weightItem.weight;
      case 'sampleWeight':
        return props.weightItem.sampleWeight;
      case 'icePercentage':
        return props.weightItem.icePercentage;
      case 'declaredIcePercentage':
        return props.weightItem.declaredIcePercentage;
      case 'deduction':
        return props.weightItem.deduction;
      case 'vehicleDeduction':
        return props.weightItem.carDeduction;
      case 'vehicleId':
        return props.weightItem.vehicle?.id;
      case 'userComment':
        return props.weightItem.userComment;
      case 'unGuttedQuantity':
        return props.weightItem.unGuttedQuantity;
      case 'amount':
        return props.weightItem.quantity;
      case 'totalAmountSample':
        return props.weightItem.totalAmountSample;
      case 'animalCount':
        return props.weightItem.animalCount;
      default:
        return undefined;
    }
  }, [props.weightItem]);

  const valuesAreEqual = (incoming: unknown, original: unknown) => {
    if (incoming === original) {
      return true;
    }
    if (incoming === undefined && original === undefined) {
      return true;
    }
    if (incoming === null && original === null) {
      return true;
    }
    if (typeof incoming === 'number' || typeof original === 'number') {
      if (incoming === undefined || incoming === null || original === undefined || original === null) {
        return false;
      }
      return Number(incoming) === Number(original);
    }
    if (typeof incoming === 'boolean' || typeof original === 'boolean') {
      if (typeof incoming !== 'boolean' || typeof original !== 'boolean') {
        return false;
      }
      return incoming === original;
    }
    if (typeof incoming === 'string' || typeof original === 'string') {
      const incomingIsNil = incoming === null || incoming === undefined;
      const originalIsNil = original === null || original === undefined;

      if (incomingIsNil || originalIsNil) {
      // equal only if both are nil
        return incomingIsNil && originalIsNil;
      }

      return String(incoming) === String(original);
    }
    return false;
  };

  useEffect(() => {
    const hasCarDeduction = props.selectedWeightNote?.weights?.some(weight => weight.carDeduction);
    const registeredVehicle = props.selectedWeightNote?.weights?.find(weight => weight.vehicle)?.vehicle;
    let registeredVehicleDeduction = props.selectedWeightNote?.weights?.find(weight => weight.vehicle)?.carDeduction;
    if (props.user.settings.wantsToPinVehicle || props.user.settings.wantsToPinVehicleBrutto) {
      const registeredVehicleId = props.selectedWeightNote?.weights?.find(weight => weight.vehicle)?.vehicle?.id;
      const vehicle = props.vehicles.find(v => v.id === registeredVehicleId);
      if (vehicle) {
        if (props.user.settings.wantsToPinVehicleBrutto) {
          registeredVehicleDeduction = vehicle.vehicleGross;
        } else if (props.user.settings.wantsToPinVehicle) {
          registeredVehicleDeduction = vehicle.vehicleTare;
        }
      }
    }
    if (props.user.settings.wantsToBeginWithVehicle && !props.weightItem && props.open && (!hasCarDeduction || !props.user.settings.wantsToPinVehicle || !props.user.settings.wantsToPinVehicleBrutto)) {
      setAutoFocusVehicle(true);
      setDeductionModalOpen(true);
    }
    if ((props.user.settings.wantsToPinVehicle || props.user.settings.wantsToPinVehicleBrutto) && registeredVehicle && registeredVehicleDeduction && !props.weightItem && props.open) {
      const newTempDeductionItem: CatchDeduction = {
        id: tempDeductions.length + 1,
        weightId: 0,
        deductionTypeId: CatchRegistrationDefaults.TRANSPORT_VEHICLE_DEDUCTION_TYPE_ID,
        deductionType: `Flutningstæki (${registeredVehicle?.vehicleRegistrationNumber})`,
        weight: registeredVehicleDeduction,
        unit: 1,
        quantity: registeredVehicleDeduction,
        shouldWithdraw: true,
        vehicleId: registeredVehicle?.id
      };
      setTempDeductions([...tempDeductions, newTempDeductionItem]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.weightItem, props.open, props.selectedWeightNote, props.user.settings.wantsToBeginWithVehicle, props.user.settings.wantsToPinVehicle]);

  const toggleDeductionModalOpen = () => {
    setAutoFocusVehicle(false);
    setDeductionModalOpen(prev => {
      const next = !prev;
      if (prev && !props.weightItem) {
        requestStockFocus();
      }
      return next;
    });
  };

  const toggleSplitModalOpen = () => {
    setSplitModalOpen(!splitModalOpen);
  };

  const toggleMoveModalOpen = () => {
    setMoveModalOpen(!moveModalOpen);
  };

  const handleSubmit = async () => {
    if (formData) {
      try {
        setLoading(true);
        const body: PostCatchBody = {
          isFromReWeighter: false,
          vehicleId: formData.vehicleId,
          vehicleDeduction: formData.vehicleDeduction,
          fishStockId: formData.fishStockId,
          fishingAreaId: formData.fishingAreaId,
          conditionId: formData.conditionId,
          storageMethodId: formData.storageMethodId,
          destinyId: formData.destinyId,
          isUnderKilogram: formData.isUnderKilogram,
          weight: formData.weight,
          sampleWeight: formData.sampleWeight,
          animalCount: showAnimalCount ? formData.animalCount : undefined,
          deduction: formData.deduction,
          icePercentage: formData.icePercentage,
          userComment: formData.userComment
        };
        const vehicleId = tempDeductions.find(deduction => deduction.deductionTypeId === CatchRegistrationDefaults.TRANSPORT_VEHICLE_DEDUCTION_TYPE_ID)?.vehicleId;
        const vehicleDeduction = tempDeductions.find(deduction => deduction.deductionTypeId === CatchRegistrationDefaults.TRANSPORT_VEHICLE_DEDUCTION_TYPE_ID)?.weight;
        if (vehicleId && vehicleDeduction !== undefined) {
          body.vehicleId = vehicleId;
          body.vehicleDeduction = vehicleDeduction;
        }
        const newWeightItem = await postWeight(body, props.selectedWeightNote.id);
        if (vehicleId && vehicleDeduction !== undefined) {
          // users registered vehicle deduction. We must refetch vehicles since DB updates LAST_BRUTTO after posting weight.
          await props.refetchVehicles();
        }
        if (tempDeductions.length > 0 && newWeightItem) {
          tempDeductions.forEach(async (deduction) => {
            // We don't want to post the vehicle deduction as a car deduction, as it is already registered as a vehicle deduction.
            if (deduction.deductionTypeId !== CatchRegistrationDefaults.TRANSPORT_VEHICLE_DEDUCTION_TYPE_ID) {
              const newDeduction: PostDeductionBody = {
                harbourId: props.user?.userPickedHarbour ?? 0,
                typeId: deduction.deductionTypeId,
                unit: deduction.unit,
                quantity: deduction.weight
              };
              await postDeduction(newDeduction, newWeightItem.data);
            }
          });
        }
        if (newWeightItem) {
          await props.setNewWeightItem(newWeightItem.data);
        }
        props.showSnackbar('Vigtun skráð!', 'success');
        if (props.user.settings.wantsToKeepWeightModalOpen){
          handleNew();
        } else {
          handleClose();
        }
        setLoading(false);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      catch (error: any) {
        setLoading(false);
        if (error instanceof SessionExpiredError || error instanceof AuthenticationUnavailableError) {
          props.showSnackbar(error.message, 'warning');
        } else if (error.response && error.response.data && typeof error.response.data !== 'object') {
          const errorMsg = error.response.data;
          props.showSnackbar(errorMsg, 'error');

        }
        else {
          logError(error);
          props.showSnackbar('Óvænt villa kom upp!', 'error');
        }
      }
    }
  };

  const handleUpdate = async () => {
    if (formData && props.weightItem) {
      if (!formData.shouldClearVehicle) {
        formData.shouldClearVehicle = false;
      }
      if (!icePercentageChanged) {
        // patch problem, we need to distinguish between if users made changes to icePercentage or not.
        formData.icePercentage = props.weightItem.icePercentage;
      }
      try {
        setLoading(true);
        const updateBody: PostCatchBody = { ...formData };
        if (!showAnimalCount) {
          delete updateBody.animalCount;
          delete updateBody.shouldClearAnimalCount;
          if (props.weightItem.animalCount !== null && props.weightItem.animalCount !== undefined) {
            updateBody.shouldClearAnimalCount = true;
          }
        }
        await patchWeight(props.weightItem?.id, updateBody);
        await props.setNewWeightItem(props.weightItem.id);
        props.showSnackbar('Vigtun uppfærð!', 'success');
        handleClose();
        setLoading(false);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      catch (error: any) {
        setLoading(false);
        if (error instanceof SessionExpiredError || error instanceof AuthenticationUnavailableError) {
          props.showSnackbar(error.message, 'warning');
        } else if (error.response && error.response.data && typeof error.response.data !== 'object') {
          const errorMsg = error.response.data;
          props.showSnackbar(errorMsg, 'error');
        }
        else {
          logError(error);
          props.showSnackbar('Óvænt villa kom upp!', 'error');
        }
        // Ekki hreinsa innslátt þegar vistun mistókst.
        return;
      }
      if (formData.weight) {
        // users changed weight so we must refetch vehicles since DB updates LAST_BRUTTO
        await props.refetchVehicles();
      }
      setFormData({});
    }
  };

  const handleFormChange = (updatedFormData: PostCatchBody) => {
    setFormData(prevFormData => {
      const nextFormData: PostCatchBody = { ...prevFormData };
      const nextRecord = nextFormData as Record<string, unknown>;
      const prevRecord = prevFormData as Record<string, unknown>;
      let hasChanges = false;

      Object.entries(updatedFormData).forEach(([key, value]) => {
        const typedKey = key as keyof PostCatchBody;
        const keyName = typedKey as string;
        const incomingValue = value as unknown;

        if (incomingValue === undefined) {
          if (keyName in nextRecord) {
            delete nextRecord[keyName];
            hasChanges = true;
          }
          return;
        }

        if (props.weightItem) {
          const originalValue = getOriginalFieldValue(typedKey);
          if (valuesAreEqual(incomingValue, originalValue)) {
            if (keyName in nextRecord) {
              delete nextRecord[keyName];
              hasChanges = true;
            }
            return;
          }
        }

        if (!valuesAreEqual(incomingValue, nextRecord[keyName])) {
          nextRecord[keyName] = incomingValue;
          hasChanges = true;
        }
      });

      if (!props.weightItem) {
        setPrefilledDefaults(prevDefaults => {
          let changed = false;
          let nextDefaults = prevDefaults;
          PREFILL_FIELDS.forEach((field) => {
            const newValue = updatedFormData[field];
            if (
              newValue !== undefined &&
              prevRecord[field] === undefined &&
              !(field in prevDefaults)
            ) {
              if (!changed) {
                nextDefaults = { ...prevDefaults };
                changed = true;
              }
              nextDefaults[field] = newValue;
            }
          });
          return changed ? nextDefaults : prevDefaults;
        });
      }

      return hasChanges ? nextFormData : prevFormData;
    });
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSelectStep = (index: number) => {
    setActiveStep(index);
  };

  const getTotalWeight = (updatedFormData: PostCatchBody) => {
    const isProductNote = props.selectedWeightNote.weightNoteType.id === WeightNoteTypes.PRODUCT_ID;
    // Use sampleWeight for PRODUCT_ID, otherwise use weight
    const getWeight = () => {
      if (isProductNote) {
        return updatedFormData.sampleWeight ?? props.weightItem?.sampleWeight ?? 0;
      }
      return updatedFormData.weight ?? props.weightItem?.weight ?? 0;
    };

    if (!props.weightItem) { // User is creating
      const tempVehicleDeduction = tempDeductions.find((deduction) => deduction.deductionTypeId === CatchRegistrationDefaults.TRANSPORT_VEHICLE_DEDUCTION_TYPE_ID);
      const percentage = (updatedFormData.icePercentage || 0) / 100;
      const deduction = updatedFormData.deduction || 0;
      const tempDeduction = tempDeductions.reduce((acc, curr) => {
        return curr.deductionTypeId !== CatchRegistrationDefaults.TRANSPORT_VEHICLE_DEDUCTION_TYPE_ID ? acc + curr.quantity : acc;
      }, 0);
      const vehicleDeduction = tempVehicleDeduction?.weight || 0;
      const weight = getWeight();
      const netWeight = weight - vehicleDeduction - deduction - tempDeduction;
      const percentageAmount = Math.round(netWeight * percentage);
      return netWeight - percentageAmount;
    } else { // User is editing
      const percentage = (updatedFormData.icePercentage ?? props.weightItem.icePercentage ?? 0) / 100;
      let deduction = updatedFormData.deduction ?? props.weightItem.deduction ?? 0;
      if ((props.weightItem.deduction === 0 || props.weightItem.deduction === undefined) && Array.isArray(props.weightItem.deductions)) {
        deduction = props.weightItem.deductions.reduce((sum, d) => sum + (d.quantity ?? 0), 0);
      }
      const vehicleDeduction = updatedFormData.vehicleDeduction ?? props.weightItem.carDeduction ?? 0;
      const weight = getWeight();
      const netWeight = weight - vehicleDeduction - deduction;
      const percentageAmount = Math.round(netWeight * percentage);
      return netWeight - percentageAmount;
    }
  };

  useEffect(() => {
    setCurrentTotalWeight(getTotalWeight(formData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, props.weightItem, props.weightItem?.deductions, tempDeductions]);

  useEffect(() => {
    if (!props.weightItem) {
      setFormData({});
      setPrefilledDefaults({});
      setActiveStep(0);
      handleTempDeductionChange([]);
      setIcePercentageChanged(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedWeightNote.id]);

  const closeHandler = () => {
    if (!loading) {
      return hasUnsavedChanges ? confirmClose() : handleClose();
    }
    return undefined;
  };

  const confirmClose = () => {
    confirm({
      title: 'Villtu loka glugganum?',
      description: `Þú ert með óvistaðar breytingar.`,
      confirmationText: 'Loka',
      cancellationText: 'Hætta við',
      confirmationButtonProps: { variant: 'contained', color: 'primary' },
      cancellationButtonProps: { variant: 'outlined' },
    })
      .then(() => handleClose())
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => { });
  };

  const handleClose = () => {
    setFormData({});
    setPrefilledDefaults({});
    setActiveStep(0);
    handleTempDeductionChange([]);
    props.toggleCatchOpen();
    setIcePercentageChanged(false);
    setAutoFocusVehicle(false);
    setProductModalOpen(false);
    autoOpenedProductCalcRef.current = false;
  };

  const handleNew = () => {
    setFormData({});
    setPrefilledDefaults({});
    setActiveStep(0);
    handleTempDeductionChange([]);
    props.resetWeightItem();
    setIcePercentageChanged(false);
    requestStockFocus();
    setAutoFocusVehicle(false);
  };

  const handleTempDeductionChange = (deductions: CatchDeduction[]) => {
    setTempDeductions(deductions)
  };

  const handleProductCalc = () => {
    // Manual toggle from the "Uppreikna" button is never the auto-open special case.
    autoOpenedProductCalcRef.current = false;
    setProductModalOpen(!productModalOpen);
  };

  // Closes only the ProductCalc modal (cancel / X). Clears the special-case flag
  // so a later manual open doesn't inherit the auto-close behaviour.
  const handleProductCalcClose = () => {
    autoOpenedProductCalcRef.current = false;
    setProductModalOpen(false);
  };

  // Confirming ProductCalc. In the auto-open special case we also close this modal,
  // otherwise we just close ProductCalc and keep the catch registration open.
  const handleProductCalcConfirm = () => {
    if (autoOpenedProductCalcRef.current) {
      autoOpenedProductCalcRef.current = false;
      handleClose();
      return;
    }
    setProductModalOpen(false);
  };

  useEffect(() => {
    if (!props.open) return;
    if (
      props.selectedWeightNote.weightNoteType.id === WeightNoteTypes.PRODUCT_ID &&
      props.weightItem &&
      (props.weightItem.deductions?.length ?? 0) > 0
    ) {
      autoOpenedProductCalcRef.current = true;
      setProductModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const requestStockFocus = useCallback(() => {
    setStockFocusTrigger(prev => prev + 1);
  }, []);

  const handleTabSkip = () => {
    // When isUnderKilogram becomes disabled in child component we need to send focus to another child component.
    // This is because we are using autoComplete which sets the focus to a disabled element which will break tabbing.
    requestAnimationFrame(() => {
      fallbackRef.current?.focus();
    });
  };

  useEffect(() => {
    if (props.open && !props.weightItem) {
      requestStockFocus();
    }
  }, [props.open, props.weightItem, requestStockFocus]);

  const hasUnsavedChanges = useMemo(() => {
    if (props.weightItem) {
      return Object.keys(formData).length > 0;
    }
    return Object.entries(formData).some(([key, value]) => {
      if (value === undefined || value === null) {
        return false;
      }
      if (PREFILL_FIELD_SET.has(key as PrefilledField)) {
        return prefilledDefaults[key as PrefilledField] !== value;
      }
      return true;
    });
  }, [formData, prefilledDefaults, props.weightItem]);

  const formDataChanges = Object.keys(formData);
  const missingRequiredFieldsInEditState = (
    (formDataChanges.includes('storageMethodId') && !formData.storageMethodId) ||
    (formDataChanges.includes('fishingAreaId') && !formData.fishingAreaId) ||
    (formDataChanges.includes('fishStockId') && !formData.fishStockId) ||
    (formDataChanges.includes('destinyId') && !formData.destinyId) ||
    (formDataChanges.includes('conditionId') && !formData.conditionId)
  );
  const conditionalButtons = () => {
    if (props.weightItem) {
      return (
        <div style={{ width: '100%', display: 'flex', flexDirection: isMobile || isTablet ? 'column' : 'row', justifyContent: 'space-between', marginTop: '1em', gap: '1em' }}>
          <Tooltip title='F8' arrow>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleNew}>
              <span>Ný aflaskráning</span>
            </Button>
          </Tooltip>
          <Button
            fullWidth
            variant="outlined"
            color="secondary"
            startIcon={<CallSplitIcon />}
            onClick={toggleSplitModalOpen}>
            <span>Skipta vigt</span>
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="warning"
            startIcon={<SwapHorizIcon />}
            onClick={toggleMoveModalOpen}>
            <span>Færa á aðra vigtarnótu</span>
          </Button>
          <LoadingButton
            fullWidth
            size="small"
            onClick={handleUpdate}
            loading={loading}
            disabled={
              loading ||
              props.user?.role.id === Number(process.env.REACT_APP_ROLE_READ_ID) ||
              Object.keys(formData)?.length === 0 ||
              getTotalWeight({ ...props.weightItem, ...formData }) <= 0 ||
              missingRequiredFieldsInEditState
            }
            endIcon={<SaveIcon />}
            color='primary'
            variant="contained"
            ref={submitButtonRef}
          >
            <span>Uppfæra</span>
          </LoadingButton>
        </div>
      );
    }

    return (
      <div style={{ marginTop: '1em', textAlign: 'right' }}>
        <LoadingButton
          fullWidth={isMobile || isTablet}
          size="small"
          onClick={handleSubmit}
          loading={loading}
          disabled={
            loading ||
            props.user?.role.id === Number(process.env.REACT_APP_ROLE_READ_ID) ||
            !formData.fishStockId ||
            !formData.fishingAreaId ||
            !formData.storageMethodId ||
            !formData.destinyId ||
            !formData.conditionId ||
            (props.selectedWeightNote.weightNoteType.id === WeightNoteTypes.PRODUCT_ID ? !formData.sampleWeight || formData.sampleWeight <= 0 : !formData.weight || formData.weight <= 0) ||
            getTotalWeight(formData) <= 0
          }
          endIcon={<CheckIcon />}
          color='success'
          variant="contained"
          ref={submitButtonRef}
        >
          <span>Staðfesta</span>
        </LoadingButton>
      </div>
    );
  }

  const steps = [
    {
      label: 'Afli',
      inputFields: (
        <CatchRegistrationCatch
          disabled={loading}
          handleFormChange={handleFormChange}
          formData={formData}
          stockInputRef={stockInputRef}
          stockFocusTrigger={stockFocusTrigger}
          allowedCombinations={props.catchCombinations}
          weight={props.weightItem}
          selectedWeightNote={props.selectedWeightNote}
          fishAreas={props.fishAreas}
          storageMethods={props.storageMethods}
          destinies={props.destinies}
          fishingStocks={props.fishingStocks}
          conditions={props.conditions}
          gears={props.gears}
          containsExported={props.selectedWeightNote.weights?.some(weight => weight.destiny?.id === CatchRegistrationDefaults.DESTINY_EXPORTED_ID) ?? false}
          onSkipTab={handleTabSkip}
        />
      )
    },
    {
      label: 'Vigtun',
      inputFields: (
        <CatchRegistrationWeight
          disabled={loading}
          handleFormChange={handleFormChange}
          formData={formData}
          setIcePercentageHasChanged={setIcePercentageChanged}
          weightItem={props.weightItem}
          selectedWeightNote={props.selectedWeightNote}
          isExported={
            formData.destinyId === CatchRegistrationDefaults.DESTINY_EXPORTED_ID ||
            (props.weightItem?.destiny?.id === CatchRegistrationDefaults.DESTINY_EXPORTED_ID && !formData.destinyId)
          }
          stockIsScallops={
            formData.fishStockId === CatchRegistrationDefaults.FISHING_STOCK_SCALLOPS_ID ||
            (props.weightItem?.fishingStock?.id === CatchRegistrationDefaults.FISHING_STOCK_SCALLOPS_ID && !formData.fishStockId)
          }
          showAnimalCount={showAnimalCount}
          forwardRef={fallbackRef}
        />
      )
    },
    {
      label: 'Frádráttur',
      inputFields: (
        <CatchRegistrationDeduction
          deductionModalOpen={deductionModalOpen}
          toggleDeductionModalOpen={toggleDeductionModalOpen}
          selectedWeightNote={props.selectedWeightNote}
          weightItem={props.weightItem}
          autoFocusVehicle={autoFocusVehicle}
          user={props.user}
          setNewWeightItem={props.setNewWeightItem}
          deductionTypes={props.deductionTypes}
          showSnackbar={props.showSnackbar}
          tempDeductions={tempDeductions}
          setTempDeductions={handleTempDeductionChange}
          vehicles={props.vehicles}
          formData={formData}
          refetchVehicles={props.refetchVehicles}
        />
      )
    }
  ];

  const handleCarouselChange = (newIndex: number) => {
    props.setIndex(newIndex);
    setFormData({});
  };

  const handleSplit = async (splitBody: PostCatchBody) => {
    if (!props.weightItem) return;
    const originalId = props.weightItem.id;
    const originalWeight = props.weightItem.weight;
    const delta = splitBody.weight ?? 0;
    if (delta <= 0 || delta > originalWeight) return;

    await postWeight(splitBody, props.selectedWeightNote.id);

    await patchWeight(originalId, { weight: originalWeight - delta });

    await props.setNewWeightItem(originalId);
  };

  const handleMove = async (weightNoteId: number, isToReweighing: boolean): Promise<void> => {
    // Moves the catch registration from the selected weightNote to the weightNote requested.
    if (!props.weightItem) {
      return;
    }
    const deductions = props.weightItem?.deductions;
    let icePercentage = props.weightItem.icePercentage ? props.weightItem.icePercentage : 0;
    if (isToReweighing) {
      icePercentage = 0;
    }
    await deleteWeight(props.weightItem.id);
    const newWeightResp = await postWeight({
      isFromReWeighter: false,
      vehicleId: props.weightItem.vehicle?.id,
      vehicleDeduction: props.weightItem.carDeduction,
      fishStockId: props.weightItem.fishingStock.id,
      fishingAreaId: props.weightItem.fishingArea.id,
      conditionId: props.weightItem.condition.id,
      storageMethodId: props.weightItem.storageMethod.id,
      destinyId: props.weightItem.destiny.id,
      isUnderKilogram: props.weightItem.underKilogram,
      weight: props.weightItem.weight,
      sampleWeight: props.weightItem.sampleWeight || 0,
      animalCount: props.weightItem.animalCount,
      icePercentage: icePercentage,
      declaredIcePercentage: props.weightItem.declaredIcePercentage,
      amount: props.weightItem.quantity,
      unGuttedQuantity: props.weightItem.unGuttedQuantity,
      userComment: props.weightItem.userComment,
    }, weightNoteId);
    if (deductions) {
      deductions.forEach(async (deduction) => {
        const newDeduction: PostDeductionBody = {
          harbourId: props.user?.userPickedHarbour ?? 0,
          typeId: deduction.deductionTypeId,
          unit: deduction.unit,
          quantity: deduction.weight
        };
        await postDeduction(newDeduction, newWeightResp?.data);
      });
    }
    await props.setNewWeightItem(newWeightResp?.data);
    handleClose();
  };

  return (
    <Dialog
      open={props.open}
      onClose={closeHandler}
      disableEscapeKeyDown={loading}
      PaperComponent={isMobile ? MobilePaperComponent : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      maxWidth='lg'
      sx={{
        '.MuiPaper-root': {
          padding: 0,
          width: 'auto'
        },
      }}
      TransitionProps={{
        onEntered: () => {
          if (props.weightItem && !productModalOpen) {
            navRef.current?.focus();
          }
        }
      }}
    >
      {props.selectedWeightNote.weightNoteType.id === WeightNoteTypes.PRODUCT_ID && props.weightItem && productModalOpen && (
        <CatchRegistrationProductCalcModal
          handleFormChange={handleFormChange}
          disabled={props.selectedWeightNote.isClosed}
          selectedWeightNote={props.selectedWeightNote}
          weightItem={props.weightItem}
          showSnackbar={props.showSnackbar}
          deductions={props.weightItem?.deductions ? props.weightItem.deductions : []}
          setWeightItem={props.setNewWeightItem}
          open={productModalOpen}
          toggleOpen={handleProductCalcClose}
          onConfirm={handleProductCalcConfirm}
        />
      )}
      {props.weightItem && (
        <>
          <CatchRegistrationSplitModal
            open={splitModalOpen}
            weightItem={props.weightItem}
            handleClose={toggleSplitModalOpen}
            handleSplit={handleSplit}
            catchCombinations={props.catchCombinations}
            fishAreas={props.fishAreas}
            storageMethods={props.storageMethods}
            destinies={props.destinies}
            fishingStocks={props.fishingStocks}
            conditions={props.conditions}
            gears={props.gears}
            selectedWeightNote={props.selectedWeightNote}
            showSnackbar={props.showSnackbar} />
          <CatchRegistrationMoveModal
            open={moveModalOpen}
            handleClose={toggleMoveModalOpen}
            selectedWeightNote={props.selectedWeightNote}
            handleMove={handleMove}
            showSnackbar={props.showSnackbar}
            user={props.user} />
        </>
      )}
      {/** Catch registration data form */}
      <CatchRegistrationFormWrapper>
        <CatchRegistrationFormHeader id="draggable-dialog-title">
          <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', alignItems: 'center', marginTop: '0.5em' }}>
            {props.weightItem ? (
              <>
                {isMobile || isTablet ? (
                  <>
                    <div>Aflaskráning</div>
                    <div>{'#' + props.weightItem.id}</div>
                  </>
                ) : (
                  <div>Aflaskráning {'#' + props.weightItem.id}</div>
                )}
              </>
            ) : (
              <div>Aflaskráning</div>
            )}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '0.5em' }}>
              <CatchRegistrationModalNavigator
                total={props.sortedWeights ? props.sortedWeights.length : 0}
                index={props.selectedCarouselIndex ?? 0}
                onChange={handleCarouselChange}
                disabled={
                  loading ||
                  !props.sortedWeights ||
                  !props.weightItem ||
                  props.selectedCarouselIndex === props.sortedWeights?.length
                }
                ariaLabel="Aflaskráning"
                ref={navRef}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: isMobile || isTablet ? 'column' : 'row', gap: isMobile || isTablet ? '1em' : '0em', justifyContent: 'center' }}>
              <Avatar
                sx={{
                  bgcolor: theme.palette.mode === "dark" ? "#272727" : 'white',
                  fontSize: 16,
                  color: theme.palette.text.primary,
                  border: '1px solid #cdcdcd',
                  fontWeight: '500',
                  height: 40,
                  width: 150,
                  borderRadius: '1em'
                }}
                variant="rounded"
              >
                {currentTotalWeight + " kg" || "0 kg"}
              </Avatar>
              {props.selectedWeightNote.weightNoteType.id === WeightNoteTypes.PRODUCT_ID && (
                <Tooltip followCursor title={props.weightItem?.deductions?.length === 0 ? 'Vantar frádrátt til að uppreikna' : ''}>
                  <div>
                    <Button
                      sx={{ borderRadius: '1em', width: 180, marginLeft: '1em' }}
                      variant={props.selectedWeightNote.isClosed ? 'outlined' : 'contained'}
                      color={props.selectedWeightNote.isClosed ? 'primary' : 'secondary'}
                      disabled={(props.weightItem?.deductions?.length === 0 || loading) || !props.weightItem}
                      startIcon={props.selectedWeightNote.isClosed ? <VisibilityIcon /> : <CalculateIcon />}
                      onClick={handleProductCalc}>
                      <span>{props.selectedWeightNote.isClosed ? 'Uppreikningur' : 'Uppreikna'}</span>
                    </Button>
                  </div>
                </Tooltip>
              )}
            </div>
            <IconButton sx={{ position: 'absolute', top: 0, right: 0 }} onClick={closeHandler} tabIndex={-1}>
              <CancelIcon fontSize='large' />
            </IconButton>
          </div>
        </CatchRegistrationFormHeader>
        <DialogContent>
          {isMobile || isTablet ? ( // conditionally render steps if screen is small otherwise use space...
            <CatchRegistrationFormBody>
              <Stepper activeStep={activeStep} orientation="vertical" sx={{ height: '100%' }}>
                {steps.map((step, index) => (
                  <Step key={step.label}>
                    <StepLabel onClick={() => handleSelectStep(index)} sx={{ cursor: 'pointer  !important' }}>
                      {step.label}
                    </StepLabel>
                    <StepContent
                      TransitionProps={{ unmountOnExit: false }}
                    >
                      <Typography variant='h6'>{step.inputFields}</Typography>
                      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        {index === steps?.length - 1 ? '' :
                          <Button
                            variant="contained"
                            onClick={handleNext}
                            size='small'
                            sx={{ mt: 1, mr: 1 }}
                          >
                            Næsta skref
                          </Button>}
                        <Button
                          disabled={index === 0}
                          onClick={handleBack}
                          size='small'
                          sx={{ mt: 1, mr: 1 }}
                        >
                          Til baka
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CatchRegistrationFormBody>) : (
            <MainRowContainer>
              <RowContainer>
                <ChildWrapper style={{ backgroundColor: theme.palette.mode === "dark" ? "#272727" : undefined }}>
                  <ChildData data-testid="catch">
                    <CatchRegistrationCatch
                      disabled={loading}
                      handleFormChange={handleFormChange}
                      formData={formData}
                      stockInputRef={stockInputRef}
                      stockFocusTrigger={stockFocusTrigger}
                      allowedCombinations={props.catchCombinations}
                      weight={props.weightItem}
                      selectedWeightNote={props.selectedWeightNote}
                      fishAreas={props.fishAreas}
                      storageMethods={props.storageMethods}
                      destinies={props.destinies}
                      fishingStocks={props.fishingStocks}
                      conditions={props.conditions}
                      gears={props.gears}
                      containsExported={props.selectedWeightNote.weights?.some(weight => weight.destiny?.id === CatchRegistrationDefaults.DESTINY_EXPORTED_ID) ?? false}
                      onSkipTab={handleTabSkip}
                    />
                  </ChildData>
                </ChildWrapper>
              </RowContainer>

              <RowContainer>
                <ChildWrapper data-testid="weight" style={{ backgroundColor: theme.palette.mode === "dark" ? "#272727" : undefined }}>
                  <ChildData>
                    <CatchRegistrationWeight
                      disabled={loading}
                      handleFormChange={handleFormChange}
                      formData={formData}
                      setIcePercentageHasChanged={setIcePercentageChanged}
                      weightItem={props.weightItem}
                      selectedWeightNote={props.selectedWeightNote}
                      isExported={
                        formData.destinyId === CatchRegistrationDefaults.DESTINY_EXPORTED_ID ||
                        (props.weightItem?.destiny?.id === CatchRegistrationDefaults.DESTINY_EXPORTED_ID && !formData.destinyId)
                      }
                      stockIsScallops={
                        formData.fishStockId === CatchRegistrationDefaults.FISHING_STOCK_SCALLOPS_ID ||
                        (props.weightItem?.fishingStock?.id === CatchRegistrationDefaults.FISHING_STOCK_SCALLOPS_ID && !formData.fishStockId)
                      }
                      showAnimalCount={showAnimalCount}
                      forwardRef={fallbackRef}
                    />
                  </ChildData>
                </ChildWrapper>
                <ChildWrapper data-testid="deduction" style={{ backgroundColor: theme.palette.mode === "dark" ? "#272727" : undefined }}>
                  <ChildData>
                    <CatchRegistrationDeduction
                      deductionModalOpen={deductionModalOpen}
                      toggleDeductionModalOpen={toggleDeductionModalOpen}
                      selectedWeightNote={props.selectedWeightNote}
                      weightItem={props.weightItem}
                      autoFocusVehicle={autoFocusVehicle}
                      user={props.user}
                      setNewWeightItem={props.setNewWeightItem}
                      deductionTypes={props.deductionTypes}
                      showSnackbar={props.showSnackbar}
                      tempDeductions={tempDeductions}
                      setTempDeductions={handleTempDeductionChange}
                      vehicles={props.vehicles}
                      formData={formData}
                      refetchVehicles={props.refetchVehicles}
                    />
                  </ChildData>
                </ChildWrapper>
              </RowContainer>
            </MainRowContainer>
          )}
          {!props.selectedWeightNote.isClosed && conditionalButtons()}
        </DialogContent>
      </CatchRegistrationFormWrapper>
    </Dialog >
  );
};

export default CatchRegistrationModal;

